/**
 * CustomersPage.tsx — Admin customer search and list screen
 *
 * Purpose:
 *   Provides a debounced search input (300 ms) filtering customers via
 *   GET /customers?search=<term>, displays results in a cursor-paginated
 *   DataTable. Row click navigates to /customers/:id for the detail view.
 *
 * Public API:
 *   - CustomersPage   Route-level page component
 *
 * Architecture Context:
 *   Uses SearchInput (AT-50.01) for debounced search and DataTable for list
 *   rendering. Customer navigation handled by react-router-dom useNavigate.
 *
 * @module packages/billing-admin/src/pages/CustomersPage
 * @since 2026-06-01 (AT-50.02 — Plan & Customer Management Screens)
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import BulkActionBar from '../components/BulkActionBar';
import ConfirmDialog from '../components/ConfirmDialog';
import DataTable, { Column } from '../components/DataTable';
import SearchInput from '../components/SearchInput';
import CustomerForm from '../components/customer/CustomerForm';
import SlidePanel from '../components/SlidePanel';
import {
  useAdminCustomers,
  useCreateAdminCustomer,
  useDeleteAdminCustomer,
  useUpdateAdminCustomer,
  type AdminCustomerSummary,
} from '../hooks/useAdminApi';

// ---------------------------------------------------------------------------
// Types (local only — shape from useAdminApi)
// ---------------------------------------------------------------------------

/**
 * Customer row shape returned by GET /customers.
 *
 * @since 2026-06-01 (AT-50.02)
 */
interface Customer extends AdminCustomerSummary {
  /** Cursor value for pagination. */
  cursor?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * CustomersPage — searchable, cursor-paginated customer list.
 *
 * @returns AdminLayout-wrapped page element
 * @since 2026-06-01 (AT-50.02)
 */
export default function CustomersPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [formCustomer, setFormCustomer] = useState<Customer | null | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const params = search ? { search } : cursor ? { cursor } : {};
  const { data, isLoading, error } = useAdminCustomers(params);
  const createCustomer = useCreateAdminCustomer();
  const updateCustomer = useUpdateAdminCustomer();
  const deleteCustomer = useDeleteAdminCustomer();

  // The API returns { customers: Customer[], nextCursor: string | null }
  const customers: Customer[] = (data as { customers?: Customer[] } | undefined)?.customers ?? [];
  const nextCursor: string | null =
    (data as { nextCursor?: string | null } | undefined)?.nextCursor ?? null;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /**
   * handleSearchChange — resets cursor when search term changes.
   *
   * @param value - New search string from SearchInput
   * @since 2026-06-01 (AT-50.02)
   */
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value);
    setCursor(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Column definitions
  // ---------------------------------------------------------------------------

  /**
   * DataTable column definitions for the customer list.
   *
   * @since 2026-06-01 (AT-50.02)
   */
  const columns: Column<Customer>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <button
          onClick={() => navigate(`/customers/${row.id}`)}
          className="text-left font-medium text-indigo-600 hover:underline"
        >
          {row.name}
        </button>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (row) => <span className="text-gray-700">{row.email}</span>,
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (row) => (
        <span className="text-gray-500">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-3">
          <button
            onClick={() => setFormCustomer(row)}
            className="text-sm text-cyan-700 hover:underline"
          >
            Edit
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="text-sm text-red-600 hover:underline"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <AdminLayout title="Customers">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Customers</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Active billing accounts</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Search customer billing profiles, open a full account overview, and move directly into credits, subscriptions, and invoice history.</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl border border-cyan-100 bg-cyan-50/80 px-4 py-3 text-sm text-cyan-950">
                <span className="block text-xs font-semibold uppercase tracking-[0.22em] text-cyan-700">Visible accounts</span>
                <span className="mt-1 block text-2xl font-semibold">{customers.length}</span>
              </div>
              <button
                onClick={() => setFormCustomer(null)}
                className="rounded-full bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300"
              >
                New Customer
              </button>
            </div>
          </div>

          <div className="mt-5">
            <SearchInput
              value={search}
              onChange={handleSearchChange}
              placeholder="Search customers by name or email"
              debounceMs={300}
            />
          </div>
        </section>

        {/* Error state */}
        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 shadow-sm">
            {(error as Error).message}
          </div>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <BulkActionBar count={selected.size} label="customer" onDelete={() => setBulkDeleteOpen(true)} onClear={() => setSelected(new Set())} />
        )}

        {/* Data table */}
        <DataTable<Customer>
          columns={columns}
          rows={customers}
          rowKey={(row) => row.id}
          loading={isLoading}
          cursor={nextCursor}
          onLoadMore={() => setCursor(nextCursor)}
          selectable
          selectedKeys={selected}
          onSelectionChange={setSelected}
          onRowClick={(row) => navigate(`/customers/${row.id}`)}
        />

        {/* Create/Edit slide panel */}
        <SlidePanel
          open={formCustomer !== undefined}
          onClose={() => setFormCustomer(undefined)}
          size="md"
          title={formCustomer === null ? 'New Customer' : `Edit: ${formCustomer?.name ?? ''}`}
          category="Customer Management"
        >
          <div className="px-6 py-6">
            <CustomerForm
              customer={formCustomer ?? undefined}
              onSubmit={async (input) => {
                if (formCustomer) {
                  await updateCustomer.mutateAsync({ customerId: formCustomer.id, input });
                } else {
                  await createCustomer.mutateAsync(input);
                }
                setFormCustomer(undefined);
              }}
              onCancel={() => setFormCustomer(undefined)}
            />
          </div>
        </SlidePanel>

        <ConfirmDialog
          open={bulkDeleteOpen}
          title={`Delete ${selected.size} Customer${selected.size !== 1 ? 's' : ''}`}
          message={<div><p>Delete <strong>{selected.size}</strong> customer{selected.size !== 1 ? 's' : ''}?</p><p className="mt-2 text-red-600 font-medium">This removes all selected billing account records and cannot be undone.</p></div>}
          confirmLabel="Delete All"
          cancelLabel="Cancel"
          onConfirm={async () => {
            setBulkDeleting(true);
            try {
              await Promise.all([...selected].map((id) => deleteCustomer.mutateAsync(id)));
              setSelected(new Set());
              setBulkDeleteOpen(false);
            } finally {
              setBulkDeleting(false);
            }
          }}
          onCancel={() => setBulkDeleteOpen(false)}
          loading={bulkDeleting}
        />

        <ConfirmDialog
          open={deleteTarget !== null}
          title="Delete Customer"
          message={`Delete ${deleteTarget?.name ?? 'this customer'}? This removes the billing account record.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => {
            if (!deleteTarget) {
              return;
            }
            void deleteCustomer.mutateAsync(deleteTarget.id).then(() => {
              setDeleteTarget(null);
            });
          }}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteCustomer.isPending}
        />
      </div>
    </AdminLayout>
  );
}
